import {
  AcademicCapIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  EnvelopeIcon,
  HeartIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  QrCodeIcon,
  SparklesIcon
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import { DIMENSIONS } from '../content'
import { anim, prog } from '../motion'
import { COLORS, Count, Shape } from '../primitives'
import { atB, inB } from './beats'
import { APPS } from './copy'
import { At, Fig, Mover, Paper, Pop, fade } from './figures'

/**
 * Das Spielbrett der Version 10. Jede Insel ist ein Ort im Unternehmen, die
 * Wege dazwischen sind Lenas Werdegang. Weltkoordinaten 2400 × 1500, die
 * Kamera fährt darüber (siehe ScrollytellingV10).
 */

export const WORLD = { w: 2400, h: 1500 }

const b = (beat, a, z = a) => inB(beat, a, z)
const t = atB

// ---------- Inseln und Wege ----------

const ISLANDS = [
  {
    id: 'cs',
    x: 270,
    y: 330,
    w: 780,
    h: 440,
    fill: '#E3EBFF',
    lip: '#C7D6FF',
    label: 'Customer Success',
    shape: ['dot', 'blue']
  },
  {
    id: 'hr',
    x: 1090,
    y: 320,
    w: 460,
    h: 450,
    fill: '#DCF7F0',
    lip: '#B3EBDD',
    label: 'HR',
    shape: ['sq', 'green']
  },
  {
    id: 'reach',
    x: 1690,
    y: 240,
    w: 600,
    h: 500,
    fill: '#FFF1D6',
    lip: '#FFDFA3',
    label: 'Karriereseite, Social Media & Messe',
    shape: ['tri', 'yellow']
  },
  {
    id: 'arena',
    x: 860,
    y: 850,
    w: 780,
    h: 470,
    fill: '#FFE6EF',
    lip: '#FFC8DB',
    label: 'Assessment',
    shape: ['dot', 'pink']
  },
  {
    id: 'pool',
    x: 1730,
    y: 900,
    w: 520,
    h: 420,
    fill: '#E3EBFF',
    lip: '#C7D6FF',
    label: 'Talentpool',
    shape: ['dot', 'blue']
  },
  {
    id: 'sales',
    x: 300,
    y: 930,
    w: 500,
    h: 380,
    fill: '#DCF7F0',
    lip: '#B3EBDD',
    label: 'Vertrieb',
    shape: ['sq', 'green']
  }
]

const PATHS = [
  'M1050 560 L1090 560',
  'M1550 540 L1690 520',
  'M1990 740 C1990 820 1800 820 1640 960',
  'M1290 770 L1290 850',
  'M1640 1110 L1730 1110',
  'M860 1130 L800 1130',
  'M640 770 L640 930'
]

// Lenas Weg über das Brett, im Finale als Linie
const TRAIL = [
  [2330, 660],
  [2000, 650],
  [1900, 860],
  [1290, 1175],
  [925, 1206],
  [1290, 820],
  [1480, 690],
  [1000, 720],
  [790, 690],
  [700, 600]
]
const MILESTONES = [
  { x: 2000, y: 650, label: 'April' },
  { x: 1290, y: 1175, label: 'Mai' },
  { x: 1480, y: 690, label: 'Juni' },
  { x: 700, y: 600, label: 'Juli' }
]

function Board() {
  return (
    <svg
      className='v10-board'
      width={WORLD.w}
      height={WORLD.h}
      viewBox={`0 0 ${WORLD.w} ${WORLD.h}`}
      aria-hidden='true'
    >
      {PATHS.map((d) => (
        <path key={d} d={d} className='v10-path' />
      ))}
      {ISLANDS.map((i) => (
        <g key={i.id}>
          <rect
            x={i.x}
            y={i.y + 16}
            width={i.w}
            height={i.h}
            rx='44'
            fill={i.lip}
          />
          <rect
            x={i.x}
            y={i.y}
            width={i.w}
            height={i.h}
            rx='44'
            fill={i.fill}
          />
        </g>
      ))}
      <polyline
        points={TRAIL.map((p) => p.join(',')).join(' ')}
        pathLength='1'
        className='v10-trail'
        style={prog(b('finale', 0.2, 0.7))}
      />
    </svg>
  )
}

function Labels() {
  return ISLANDS.map((i) => (
    <At key={i.id} x={i.x + 28} y={i.y + 26} className='v10-label-at'>
      <span className='v10-label'>
        <Shape kind={i.shape[0]} color={i.shape[1]} size={14} />
        {i.label}
      </span>
    </At>
  ))
}

// ---------- Möbel ----------

function Desk({ x, y, w, h = 56, appear }) {
  return (
    <div
      className='v10-desk'
      style={{
        left: x,
        top: y,
        width: w,
        height: h,
        ...(appear ? anim({ at: appear, y: 20, s: 0.6 }) : null)
      }}
    >
      <i />
    </div>
  )
}

Desk.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  w: PropTypes.number.isRequired,
  h: PropTypes.number,
  appear: PropTypes.arrayOf(PropTypes.number)
}

function Laptop({ x, y }) {
  return (
    <At x={x} y={y}>
      <span className='v10-laptop' />
    </At>
  )
}

Laptop.propTypes = { x: PropTypes.number, y: PropTypes.number }

/** Leerer Platz im Team. `defined` zeigt, ab wann die Lücke ein Profil hat. */
function Gap({ x, y, show, defined, hide }) {
  return (
    <At x={x} y={y}>
      <div
        className='v10-gap'
        style={
          show
            ? anim({ at: show, out: hide, y: 0, s: 0.4 })
            : fade([-1, 0], hide)
        }
      >
        <svg viewBox='0 0 60 72' width='56' height='67' aria-hidden='true'>
          <circle cx='30' cy='34' r='25' className='v10-gap__ring' />
          {defined && (
            <circle
              cx='30'
              cy='34'
              r='25'
              className='v10-gap__def'
              style={fade(defined)}
            />
          )}
          <text x='30' y='42' textAnchor='middle'>
            ?
          </text>
        </svg>
      </div>
    </At>
  )
}

Gap.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  show: PropTypes.arrayOf(PropTypes.number),
  defined: PropTypes.arrayOf(PropTypes.number),
  hide: PropTypes.arrayOf(PropTypes.number)
}

// ---------- Customer Success ----------

const TEAM = [
  { id: 'tina', name: 'Tina', kind: 'tri', color: 'yellow', x: 430 },
  { id: 'kai', kind: 'sq', color: 'green', x: 520 },
  { id: 'noah', kind: 'dot', color: 'blue', x: 610 },
  { id: 'elif', kind: 'tri', color: '#4461D3', x: 800 },
  { id: 'sam', kind: 'sq', color: '#546ED4', x: 890 }
]
const SEAT = { x: 700, y: 600 }
const SEAT2 = { x: 985, y: 600 }
const TEAM_Y = 600
const stressTeam = [t('intro', 0.4), t('luecke', 0.2)]
const cheerTeam = b('start', 0.5, 0.58)

const TICKETS = Array.from({ length: 9 }, (_, i) => ({
  tone: ['yellow', 'pink', 'white'][i % 3],
  r: ((i * 37) % 9) - 4,
  at:
    i < 5
      ? b('intro', 0.15 + i * 0.14, 0.25 + i * 0.14)
      : b('luecke', (i - 5) * 0.08, 0.08 + (i - 5) * 0.08),
  out: b('start', 0.62 + (8 - i) * 0.03, 0.66 + (8 - i) * 0.03)
}))

function TeamBack() {
  return (
    <>
      <Gap
        x={SEAT.x}
        y={SEAT.y}
        defined={b('profil', 0.3, 0.42)}
        hide={b('start', 0.38, 0.44)}
      />
      <Gap
        x={SEAT2.x}
        y={SEAT2.y}
        show={b('pool', 0.12, 0.2)}
        hide={b('pool', 0.74, 0.8)}
      />
      {TEAM.map((m, i) => (
        <At key={m.id} x={m.x} y={TEAM_Y}>
          <Fig
            kind={m.kind}
            color={m.color}
            delay={i * 0.4}
            stress={stressTeam}
            cheer={cheerTeam}
          >
            <Pop
              at={b('analyse', 0.12 + i * 0.03, 0.2 + i * 0.03)}
              out={b('analyse', 0.62, 0.7)}
            >
              <span className='v10-miniphone'>
                <i style={{ width: `${40 + ((i * 23) % 50)}%` }} />
              </span>
            </Pop>
            {GROWTH[m.id] && <GrowthPill {...GROWTH[m.id]} />}
          </Fig>
        </At>
      ))}
      <Seated
        x={SEAT.x}
        fig={{ kind: 'dot', color: 'pink', name: 'Lena', delay: 0.7 }}
        at={b('start', 0.4, 0.48)}
        grow={b('wachsen', 0.3, 0.5)}
      >
        <GrowthPill {...GROWTH.lena} />
      </Seated>
      <Seated
        x={SEAT2.x}
        fig={{ kind: 'tri', color: 'yellow', name: 'Aylin', delay: 0.2 }}
        at={b('pool', 0.74, 0.82)}
      />
    </>
  )
}

// Abgleich mit dem neuen Profil, wie im Panel daneben
const GROWTH = {
  lena: { tone: 'green', text: 'Passt weiter', at: 0.68 },
  kai: { tone: 'yellow', text: 'Schulung', at: 0.72, icon: true },
  noah: { tone: 'pink', text: 'Gespräch', at: 0.76 }
}

function GrowthPill({ tone, text, at, icon }) {
  return (
    <Pop at={b('wachsen', at, at + 0.06)} out={b('pool', 0, 0.08)}>
      <span className={`v10-pill v10-pill--${tone}`}>
        {icon && <AcademicCapIcon width={13} height={13} />}
        {text}
      </span>
    </Pop>
  )
}

GrowthPill.propTypes = {
  tone: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  at: PropTypes.number.isRequired,
  icon: PropTypes.bool
}

function Seated({ x, y = TEAM_Y, fig, at, grow, children }) {
  return (
    <Mover
      from={{ x, y, s: 0.6, o: 0 }}
      keys={[{ at, s: 1, o: 1 }, ...(grow ? [{ at: grow, s: 1.14 }] : [])]}
    >
      <Fig {...fig} cheer={at}>
        {children}
      </Fig>
    </Mover>
  )
}

Seated.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number,
  fig: PropTypes.object.isRequired,
  at: PropTypes.arrayOf(PropTypes.number).isRequired,
  grow: PropTypes.arrayOf(PropTypes.number),
  children: PropTypes.node
}

function TeamFront() {
  return (
    <>
      <Desk x={370} y={590} w={580} />
      <Desk x={950} y={590} w={80} appear={b('pool', 0.04, 0.14)} />
      {TEAM.map((m) => (
        <Laptop key={m.id} x={m.x + 44} y={592} />
      ))}
      <At x={746} y={592} className='v10-pile'>
        {TICKETS.map((k, i) => (
          <span
            key={i}
            className={`v10-ticket v10-ticket--${k.tone}`}
            style={{
              bottom: i * 9,
              rotate: `${k.r}deg`,
              ...anim({ at: k.at, out: k.out, y: -40, oy: -20 })
            }}
          />
        ))}
      </At>
      <Plant />
      <Calendar />
      <ProfileSign />
      <At x={TEAM[0].x} y={TEAM_Y - 70}>
        <Bubble
          at={b('luecke', 0.26, 0.34)}
          out={b('luecke', 0.62, 0.7)}
          tone='yellow'
        >
          Wir brauchen Verstärkung!
        </Bubble>
        <Bubble
          at={b('start', 0.52, 0.6)}
          out={b('wachsen', 0.02, 0.08)}
          tone='pink'
        >
          Willkommen, Lena!
        </Bubble>
      </At>
    </>
  )
}

function Plant() {
  return (
    <At x={928} y={592} className='v10-plant-at'>
      <div className='v10-plant' style={prog(b('wachsen', 0.25, 0.6))}>
        <span className='v10-plant__stem' />
        <span className='v10-plant__leaf is-l' />
        <span className='v10-plant__leaf is-r' />
        <span className='v10-plant__pot' />
      </div>
    </At>
  )
}

function Calendar() {
  return (
    <At x={330} y={470}>
      <div className='v10-cal'>
        <b>Feb</b>
        <span style={fade([-1, 0], b('wachsen', 0.1, 0.18))}>2025</span>
        <span style={fade(b('wachsen', 0.14, 0.22))}>2026</span>
      </div>
    </At>
  )
}

function ProfileSign() {
  return (
    <At x={SEAT.x} y={500}>
      <div
        className='v10-sign'
        style={anim({
          at: b('profil', 0.36, 0.48),
          out: b('start', 0.3, 0.38),
          y: 20,
          s: 0.5
        })}
      >
        <small>Anforderungsprofil</small>
        {DIMENSIONS.map((d) => (
          <span key={d.name} className='v10-sign__beam'>
            <i
              style={{
                left: `${d.trans[0]}%`,
                width: `${d.trans[1] - d.trans[0]}%`
              }}
              className='is-trans'
            />
            <i
              style={{
                left: `${d.ideal[0]}%`,
                width: `${d.ideal[1] - d.ideal[0]}%`
              }}
            />
          </span>
        ))}
      </div>
    </At>
  )
}

// ---------- HR ----------

const MARA = { x: 1220, y: 600 }
const GUEST = { x: 1480, y: 690 }

function HrBack() {
  return (
    <>
      <At x={MARA.x} y={MARA.y}>
        <Fig
          kind='sq'
          color='blue'
          name='Mara'
          nameAt={b('luecke', 0.6, 0.7)}
          delay={1.1}
          stress={b('flut', 0.35, 0.5)}
          cheer={b('games', 0.04, 0.12)}
          extra={<Headset at={b('support', 0.1, 0.2)} />}
        >
          <Pop
            className='is-west'
            at={b('gespraech', 0.14, 0.22)}
            out={b('gespraech', 0.66, 0.74)}
          >
            <Bubble tone='blue' inline>
              Erzähl mal von einem schwierigen Kundengespräch.
            </Bubble>
          </Pop>
        </Fig>
      </At>
    </>
  )
}

function Headset({ at }) {
  return (
    <g style={fade(at)}>
      <path
        d='M10 30 C10 6 50 6 50 30'
        fill='none'
        stroke={COLORS.ink}
        strokeWidth='3.5'
      />
      <rect x='5' y='26' width='9' height='14' rx='4' fill={COLORS.ink} />
      <rect x='46' y='26' width='9' height='14' rx='4' fill={COLORS.ink} />
      <path
        d='M10 40 Q12 52 24 52'
        fill='none'
        stroke={COLORS.ink}
        strokeWidth='2.5'
      />
    </g>
  )
}

Headset.propTypes = { at: PropTypes.arrayOf(PropTypes.number) }

function Monitor() {
  return (
    <At x={1360} y={592}>
      <div className='v10-monitor'>
        <div className='v10-monitor__screen'>
          <span className='v10-monitor__bar' />
          <span className='v10-monitor__line' />
          <span className='v10-monitor__line is-short' />
          <span className='v10-monitor__line' />
          <span
            className='v10-monitor__layer v10-monitor__new'
            style={fade(b('hr', 0.1, 0.2), b('analyse', 0.3, 0.4))}
          >
            + Neue Stelle
          </span>
          <span
            className='v10-monitor__layer v10-monitor__pdf'
            style={fade(b('report', 0.2, 0.3), b('gespraech', 0.8, 0.9))}
          >
            <b>91</b>
            <small>Passungsprofil</small>
          </span>
          <span
            className='v10-monitor__layer v10-monitor__ats'
            style={fade(b('ats', 0.1, 0.2), b('ats', 0.9, 1))}
          >
            <i />
            <i className='is-side' />
          </span>
        </div>
        <span className='v10-monitor__foot' />
      </div>
    </At>
  )
}

function HrFront() {
  return (
    <>
      <Desk x={1130} y={590} w={340} />
      <Monitor />
      <Plane
        from={{ x: TEAM[0].x + 30, y: 520 }}
        via={{ x: 860, y: 380 }}
        to={{ x: MARA.x - 20, y: 520 }}
        at={b('luecke', 0.36, 0.62)}
      />
      <Plane
        from={{ x: MARA.x + 20, y: 520 }}
        via={{ x: 1060, y: 380 }}
        to={{ x: 820, y: 600 }}
        at={b('ats', 0.6, 0.86)}
        label='Zusage'
      />
    </>
  )
}

function Plane({ from, via, to, at, label }) {
  const [s, e] = at
  const m = s + (e - s) / 2
  return (
    <Mover
      from={{ ...from, o: 0 }}
      keys={[
        { at: [s, s + 0.002], o: 1 },
        { at: [s, m], x: via.x, y: via.y },
        { at: [m, e], x: to.x, y: to.y },
        { at: [e, e + 0.004], o: 0 }
      ]}
    >
      <span className='v10-plane'>
        <PaperAirplaneIcon width={30} height={30} />
        {label && <b>{label}</b>}
      </span>
    </Mover>
  )
}

Plane.propTypes = {
  from: PropTypes.object.isRequired,
  via: PropTypes.object.isRequired,
  to: PropTypes.object.isRequired,
  at: PropTypes.arrayOf(PropTypes.number).isRequired,
  label: PropTypes.string
}

// Daten aus der Anforderungsanalyse fliegen vom Team zu HR
function AnalysisDots() {
  return TEAM.map((m, i) => {
    const s = 0.5 + i * 0.03
    return (
      <Mover
        key={m.id}
        from={{ x: m.x, y: 500, o: 0 }}
        keys={[
          { at: b('analyse', s, s + 0.02), o: 1 },
          { at: b('analyse', s, s + 0.18), x: 1360, y: 520 },
          { at: b('analyse', s + 0.17, s + 0.2), o: 0 }
        ]}
      >
        <span
          className='v10-dot'
          style={{ background: COLORS[m.color] || m.color }}
        />
      </Mover>
    )
  })
}

// ---------- Die Flut ----------

const rnd = (i, k) => {
  const v = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453
  return v - Math.floor(v)
}

const PAPERS = Array.from({ length: 34 }, (_, i) => ({
  x: 1130 + rnd(i, 1) * 360,
  y: 560 + rnd(i, 2) * 150,
  r: (rnd(i, 3) - 0.5) * 50,
  at: 0.12 + (i / 34) * 0.58,
  tone: i % 7 === 3 ? 'ai' : undefined
}))

const NOISE = [
  { x: 1150, y: 470, text: 'Von KI geschrieben', icon: true, at: 0.36 },
  { x: 1330, y: 380, text: 'teamfähig, belastbar, motiviert', at: 0.44 },
  { x: 1200, y: 760, text: 'Lebenslauf_final_v3.pdf', at: 0.52 },
  { x: 1440, y: 735, text: 'Copy & Paste aus der Anzeige', at: 0.6 }
]
const BOX = { x: 1120, y: 712 }

function Flood() {
  return (
    <>
      <At x={BOX.x} y={BOX.y}>
        <div
          className='v10-box'
          style={anim({ at: b('games', 0.02, 0.08), y: 20, s: 0.5 })}
        >
          <span>Lebensläufe</span>
        </div>
      </At>
      {PAPERS.map((p, i) => {
        const a = p.at
        const tidy = 0.02 + i * 0.002
        return (
          <Mover
            key={i}
            from={{ x: p.x + (rnd(i, 4) - 0.5) * 300, y: 120, o: 0 }}
            keys={[
              { at: b('flut', a, a + 0.02), o: 1 },
              { at: b('flut', a, a + 0.1), x: p.x, y: p.y },
              {
                at: b('games', tidy, tidy + 0.08),
                x: BOX.x,
                y: BOX.y - 20,
                s: 0.3
              },
              { at: b('games', tidy + 0.06, tidy + 0.09), o: 0 }
            ]}
          >
            <Paper tone={p.tone} rotate={p.r} />
          </Mover>
        )
      })}
      {NOISE.map((n) => (
        <At key={n.text} x={n.x} y={n.y}>
          <span
            className={clsx('v10-noise', n.icon && 'is-ai')}
            style={anim({
              at: b('flut', n.at, n.at + 0.06),
              out: b('games', 0, 0.06),
              y: 14,
              s: 0.6
            })}
          >
            {n.icon && <SparklesIcon width={14} height={14} />}
            {n.text}
          </span>
        </At>
      ))}
      <At x={1440} y={470}>
        <span
          className='v10-counter'
          style={anim({
            at: b('flut', 0.1, 0.18),
            out: b('games', 0.0, 0.08),
            y: 14,
            s: 0.6
          })}
        >
          <Count to={APPS} at={b('flut', 0.12, 0.72)} />
          <small>Bewerbungen</small>
        </span>
      </At>
    </>
  )
}

// ---------- Suche: Plakat, Messe, Link ----------

function Reach() {
  return (
    <>
      <At x={1870} y={640}>
        <div className='v10-billboard'>
          <div className='v10-billboard__board'>
            <span className='v10-billboard__gap'>?</span>
            <div>
              <small>Wir suchen dich</small>
              <b>Customer Success Manager:in</b>
              <span className='v10-billboard__qr'>
                <QrCodeIcon width={16} height={16} /> Scannen und matchen
              </span>
            </div>
          </div>
          <i className='v10-billboard__post is-l' />
          <i className='v10-billboard__post is-r' />
        </div>
      </At>
      <At x={2160} y={640}>
        <div className='v10-booth'>
          <div className='v10-booth__roof' />
          <div className='v10-booth__body'>
            <QrCodeIcon width={34} height={34} />
            <b>Messe</b>
          </div>
        </div>
      </At>
      {[
        { x: 1780, y: 330, icon: LinkIcon, at: 0.3 },
        { x: 2010, y: 300, icon: HeartIcon, at: 0.36 },
        { x: 2240, y: 330, icon: QrCodeIcon, at: 0.42 }
      ].map((s) => (
        <At key={s.x} x={s.x} y={s.y}>
          <span
            className='v10-social'
            style={anim({ at: b('link', s.at, s.at + 0.08), y: 20, s: 0.4 })}
          >
            <s.icon width={18} height={18} />
          </span>
        </At>
      ))}
    </>
  )
}

// ---------- Assessment ----------

// Dieselben Spiele wie im GamePanel daneben
const GAMES = [
  { title: 'Codeknacker', dim: 'Merkfähigkeit', kind: 'sq', color: 'green' },
  {
    title: 'Gefühlschaos',
    dim: 'Emotionswahrnehmung',
    kind: 'dot',
    color: 'pink'
  },
  {
    title: 'Fliegender Händler',
    dim: 'Risikobereitschaft',
    kind: 'tri',
    color: 'yellow'
  }
]

function GameTiles() {
  return GAMES.map((g, i) => (
    <At key={g.title} x={1030 + i * 220} y={1010}>
      <div
        className='v10-game'
        style={{
          '--d': `${-i * 0.7}s`,
          ...anim({
            at: b('games', 0.24 + i * 0.05, 0.32 + i * 0.05),
            y: 30,
            s: 0.5
          })
        }}
      >
        <Shape kind={g.kind} color={g.color} size={34} />
        <b>{g.title}</b>
        <small>{g.dim}</small>
      </div>
    </At>
  ))
}

function Scanner() {
  return (
    <div
      className='v10-scan'
      style={{
        ...prog(b('magic', 0.25, 0.7)),
        ...fade(b('magic', 0.2, 0.26), b('magic', 0.7, 0.76))
      }}
    >
      <span className='v10-scan__head'>
        <SparklesIcon width={14} height={14} /> Abgleich
      </span>
    </div>
  )
}

const PODIUM = [34, 22, 12]

function Podium() {
  return PODIUM.map((h, r) => (
    <At key={r} x={listX(r)} y={LIST_Y}>
      <span
        className='v10-step'
        style={{ height: h, ...anim({ at: b('liste', 0.3, 0.4), y: 10 }) }}
      />
    </At>
  ))
}

// ---------- Talente ----------

const CANDIDATES = [
  { id: 'lena', name: 'Lena', kind: 'dot', color: 'pink', fit: 91 },
  { id: 'aylin', name: 'Aylin', kind: 'tri', color: 'yellow', fit: 86 },
  { id: 'mo', kind: 'dot', color: 'green', fit: 82 },
  { id: 'tom', kind: 'sq', color: '#B2B4B6', fit: 66 },
  { id: 'jonas', name: 'Jonas', kind: 'sq', color: '#4461D3', fit: 62 },
  { id: 'sara', kind: 'tri', color: '#C9A7F2', fit: 58 },
  { id: 'ida', kind: 'tri', color: 'green', fit: 55 },
  { id: 'finn', kind: 'sq', color: 'yellow', fit: 51 },
  { id: 'ben', kind: 'dot', color: '#7FD3E6', fit: 47 },
  { id: 'lia', kind: 'dot', color: '#546ED4', fit: 44 },
  { id: 'nina', kind: 'sq', color: '#F2B48F', fit: 39 },
  { id: 'paul', kind: 'tri', color: '#B2B4B6', fit: 35 }
]

const REACH_SLOTS = [
  ...[1760, 1850, 1940, 2030, 2120, 2210].map((x) => ({ x, y: 660 })),
  ...[1800, 1890, 1980, 2070, 2160, 2250].map((x) => ({ x, y: 712 }))
]
const ARENA_SLOTS = [
  ...[960, 1070, 1180, 1290, 1400, 1510].map((x) => ({ x, y: 1175 })),
  ...[1015, 1125, 1235, 1345, 1455, 1565].map((x) => ({ x, y: 1260 }))
]
// Wer wo steht, bunt gemischt, damit das Sortieren sichtbar wird
const REACH_OF = [8, 3, 0, 10, 5, 1, 7, 11, 2, 6, 9, 4]
const ARENA_OF = [3, 7, 0, 10, 5, 1, 8, 11, 2, 6, 9, 4]
const LIST_Y = 1240
const listX = (r) => 925 + r * 60
const listPos = (r) => ({ x: listX(r), y: LIST_Y - (PODIUM[r] || 0) })

const POOL_GROUP = ['aylin', 'mo', 'sara', 'ida']
const SWIM = {
  aylin: { x: 1870, y: 1150 },
  mo: { x: 1970, y: 1192 },
  sara: { x: 2070, y: 1140 },
  ida: { x: 2150, y: 1196 }
}
const LEAVING = ['tom', 'finn', 'ben', 'lia', 'nina', 'paul']
const JONAS_SEAT = { x: 630, y: 1160 }

const fitTone = (v) => (v > 70 ? 'green' : v > 40 ? 'yellow' : 'grey')

function Candidate({ c, i }) {
  const reach = REACH_SLOTS[REACH_OF[i]]
  const arena = ARENA_SLOTS[ARENA_OF[i]]
  const list = listPos(i)
  const keys = []

  // Ankommen über Link, Plakat und Messe
  const a = 0.14 + i * 0.022
  keys.push({ at: b('link', a, a + 0.04), o: 1 })
  keys.push({ at: b('link', a, a + 0.24), x: reach.x, y: reach.y })

  // Runter zum Assessment
  const g = 0.06 + i * 0.012
  const via = { x: 1800 + (i % 4) * 36, y: 860 + (i % 3) * 20 }
  keys.push({ at: b('games', g, g + 0.1), x: via.x, y: via.y })
  keys.push({ at: b('games', g + 0.1, g + 0.22), x: arena.x, y: arena.y })

  // Sortiert nach Passung
  const s = 0.08 + i * 0.012
  keys.push({ at: b('liste', s, s + 0.22), x: list.x, y: list.y })

  if (c.id === 'lena') {
    keys.push({ at: b('report', 0.06, 0.2), x: 1290, y: 820 })
    keys.push({ at: b('report', 0.2, 0.32), x: GUEST.x, y: GUEST.y })
    keys.push({ at: b('team', 0.04, 0.18), x: 1000, y: 720 })
    keys.push({ at: b('team', 0.18, 0.3), x: 790, y: 690 })
    keys.push({ at: b('start', 0.3, 0.42), x: 730, y: 660 })
    keys.push({ at: b('start', 0.38, 0.44), o: 0 })
  }
  if (c.id === 'jonas') {
    keys.push({ at: b('kreuz', 0.12, 0.3), x: 880, y: 1250 })
    keys.push({ at: b('kreuz', 0.3, 0.44), x: JONAS_SEAT.x + 20, y: 1250 })
    keys.push({ at: b('kreuz', 0.44, 0.5), o: 0 })
  }
  const pk = POOL_GROUP.indexOf(c.id)
  if (pk >= 0) {
    const p = 0.3 + pk * 0.03
    const swim = SWIM[c.id]
    keys.push({ at: b('absagen', p, p + 0.14), x: 1700, y: 1250 })
    keys.push({ at: b('absagen', p + 0.14, p + 0.26), x: swim.x, y: 1060 })
    keys.push({ at: b('absagen', p + 0.24, p + 0.28), o: 0 })
  }
  const lk = LEAVING.indexOf(c.id)
  if (lk >= 0) {
    const p = 0.3 + lk * 0.025
    keys.push({ at: b('absagen', p, p + 0.3), x: 1000 + lk * 90, y: 1560 })
    keys.push({ at: b('absagen', p + 0.22, p + 0.3), o: 0 })
  }

  const isLena = c.id === 'lena'
  return (
    <Mover
      from={{ x: 2470, y: reach.y, o: 0 }}
      keys={keys}
      className={clsx(isLena && 'v10-lena')}
    >
      <Fig
        kind={c.kind}
        color={c.color}
        size={50}
        delay={i * 0.31}
        name={isLena ? 'Lena' : c.id === 'jonas' ? 'Jonas' : undefined}
        nameAt={
          isLena
            ? b('link', 0.3, 0.38)
            : c.id === 'jonas'
              ? b('kreuz', 0.05, 0.12)
              : undefined
        }
        cheer={isLena ? b('report', 0.3, 0.38) : undefined}
      >
        <Pop
          at={b('games', 0.32 + i * 0.01, 0.38 + i * 0.01)}
          out={b('magic', 0.04, 0.1)}
          className='v10-pop--side'
        >
          <span className='v10-miniphone is-game'>
            <Shape
              kind={GAMES[i % 3].kind}
              color={GAMES[i % 3].color}
              size={12}
            />
          </span>
        </Pop>
        <Pop
          at={b('talente', 0.24 + i * 0.025, 0.3 + i * 0.025)}
          out={b('magic', 0.04, 0.1)}
        >
          <span className='v10-strength'>
            <Shape
              kind={['dot', 'tri', 'sq'][i % 3]}
              color={['pink', 'yellow', 'green'][i % 3]}
              size={12}
            />
          </span>
        </Pop>
        <Pop
          at={b('magic', scanAt(arena.x), scanAt(arena.x) + 0.05)}
          out={isLena ? b('report', 0.4, 0.48) : b('absagen', 0.06, 0.12)}
        >
          <span className={`v10-pill v10-pill--${fitTone(c.fit)}`}>
            {c.fit}
          </span>
        </Pop>
        {!isLena && c.id !== 'jonas' && (
          <Pop
            at={b('absagen', 0.1 + (i % 6) * 0.015, 0.16 + (i % 6) * 0.015)}
            out={b('absagen', 0.5, 0.58)}
          >
            <span className='v10-envelope'>
              <EnvelopeIcon width={14} height={14} />
              <Shape kind='dot' color='pink' size={8} />
            </span>
          </Pop>
        )}
        {isLena && (
          <Pop
            at={b('regeln', 0.2, 0.3)}
            out={b('regeln', 0.86, 0.94)}
            className='v10-pop--side is-left'
          >
            <span className='v10-lens'>
              <MagnifyingGlassIcon width={18} height={18} />
            </span>
          </Pop>
        )}
        {isLena && (
          <Pop at={b('gespraech', 0.32, 0.4)} out={b('gespraech', 0.66, 0.74)}>
            <span className='v10-bubble v10-bubble--pink is-inline'>
              <ChatBubbleOvalLeftEllipsisIcon width={18} height={18} />
            </span>
          </Pop>
        )}
        {c.id === 'jonas' && (
          <Pop at={b('kreuz', 0.16, 0.24)} out={b('kreuz', 0.62, 0.7)}>
            <span className='v10-pill v10-pill--green'>87 · Vertrieb</span>
          </Pop>
        )}
      </Fig>
    </Mover>
  )
}

Candidate.propTypes = {
  c: PropTypes.object.isRequired,
  i: PropTypes.number.isRequired
}

const scanAt = (x) => 0.25 + (0.45 * (x - 900)) / 720

function Candidates() {
  return CANDIDATES.map((c, i) => <Candidate key={c.id} c={c} i={i} />)
}

// ---------- Talentpool ----------

function Pool() {
  return (
    <At x={1990} y={1270}>
      <div className='v10-pool'>
        <i className='v10-pool__wave' />
        <i className='v10-pool__wave is-2' />
      </div>
    </At>
  )
}

function Swimmers() {
  return POOL_GROUP.map((id, k) => {
    const c = CANDIDATES.find((x) => x.id === id)
    const p = SWIM[id]
    const s = 0.54 + k * 0.03
    const keys = [{ at: b('absagen', s, s + 0.06), o: 1 }]
    if (id === 'aylin') keys.push({ at: b('pool', 0.22, 0.28), o: 0 })
    return (
      <Mover key={id} from={{ x: p.x, y: p.y, o: 0 }} keys={keys}>
        <div className='v10-swim' style={{ '--d': `${-k * 0.9}s` }}>
          <div className='v10-swim__clip'>
            <Fig kind={c.kind} color={c.color} size={50} delay={k} />
          </div>
          <span className='v10-swim__ring' />
        </div>
      </Mover>
    )
  })
}

// Aylin steigt aus dem Pool und geht zur neuen Lücke
function AylinWalk() {
  return (
    <Mover
      from={{ x: 1860, y: 1050, o: 0 }}
      keys={[
        { at: b('pool', 0.24, 0.3), o: 1 },
        { at: b('pool', 0.3, 0.42), x: 1700, y: 860 },
        { at: b('pool', 0.42, 0.58), x: 1150, y: 760 },
        { at: b('pool', 0.58, 0.7), x: 1000, y: 690 },
        { at: b('pool', 0.7, 0.76), o: 0 }
      ]}
    >
      <Fig kind='tri' color='yellow' size={50} name='Aylin' delay={0.3}>
        <Pop at={b('pool', 0.28, 0.34)} out={b('pool', 0.54, 0.6)}>
          <span className='v10-bubble v10-bubble--yellow is-inline'>
            Passt schon, ich komme!
          </span>
        </Pop>
      </Fig>
    </Mover>
  )
}

// Neue Talente kommen über den öffentlichen Link direkt in den Pool
function Newcomers() {
  return [
    { kind: 'sq', color: 'pink', x: 2120, d: 0 },
    { kind: 'dot', color: 'yellow', x: 2190, d: 0.06 }
  ].map((f, k) => (
    <Mover
      key={k}
      from={{ x: 2330, y: 640, o: 0 }}
      keys={[
        { at: b('pool', 0.36 + f.d, 0.4 + f.d), o: 1 },
        { at: b('pool', 0.36 + f.d, 0.56 + f.d), x: 2210, y: 880 },
        { at: b('pool', 0.56 + f.d, 0.72 + f.d), x: f.x, y: 1300 }
      ]}
    >
      <Fig kind={f.kind} color={f.color} size={44} delay={k}>
        {k === 0 && (
          <Pop at={b('pool', 0.6, 0.68)}>
            <span className='v10-pill v10-pill--blue'>
              <LinkIcon width={12} height={12} /> Neu über den Einladungslink
            </span>
          </Pop>
        )}
      </Fig>
    </Mover>
  ))
}

// ---------- Vertrieb ----------

const SALES_TEAM = [
  { kind: 'dot', color: 'yellow', x: 430 },
  { kind: 'tri', color: 'blue', x: 530 },
  { kind: 'sq', color: 'pink', x: 730 }
]

function SalesBack() {
  const sit = b('kreuz', 0.48, 0.56)
  return (
    <>
      <Gap x={JONAS_SEAT.x} y={JONAS_SEAT.y} hide={b('kreuz', 0.46, 0.52)} />
      {SALES_TEAM.map((m, i) => (
        <At key={m.x} x={m.x} y={JONAS_SEAT.y}>
          <Fig
            kind={m.kind}
            color={m.color}
            delay={i * 0.5 + 0.2}
            cheer={sit}
          />
        </At>
      ))}
      <Seated
        x={JONAS_SEAT.x}
        y={JONAS_SEAT.y}
        fig={{ kind: 'sq', color: '#4461D3', name: 'Jonas', delay: 0.9 }}
        at={sit}
      />
    </>
  )
}

function SalesFront() {
  return (
    <>
      <Desk x={380} y={1150} w={400} />
      {SALES_TEAM.map((m) => (
        <Laptop key={m.x} x={m.x + 44} y={1152} />
      ))}
    </>
  )
}

// ---------- Konfetti zum ersten Tag ----------

const CONFETTI = Array.from({ length: 18 }, (_, i) => {
  const ang = (i / 18) * Math.PI * 2
  const dist = 90 + rnd(i, 7) * 90
  return {
    kind: ['tri', 'sq', 'dot'][i % 3],
    color: ['yellow', 'green', 'pink', 'blue'][i % 4],
    dx: Math.cos(ang) * dist,
    dy: Math.sin(ang) * dist * 0.7 - 40,
    r: rnd(i, 8) * 360
  }
})

function Confetti({ x, y, at }) {
  const [s, e] = at
  return CONFETTI.map((c, i) => (
    <Mover
      key={i}
      from={{ x, y, o: 0, s: 0.4 }}
      keys={[
        { at: [s, s + 0.002], o: 1 },
        { at: [s, e], x: x + c.dx, y: y + c.dy, s: 1 },
        { at: [e - (e - s) * 0.3, e], o: 0 }
      ]}
    >
      <span className='v10-confetti' style={{ rotate: `${c.r}deg` }}>
        <Shape kind={c.kind} color={c.color} size={16} />
      </span>
    </Mover>
  ))
}

// ---------- Meilensteine im Finale ----------

function Milestones() {
  return MILESTONES.map((m, i) => (
    <At key={m.label} x={m.x} y={m.y - 80}>
      <span
        className='v10-flag'
        style={anim({
          at: b('finale', 0.3 + i * 0.1, 0.38 + i * 0.1),
          y: 20,
          s: 0.5
        })}
      >
        {m.label}
      </span>
    </At>
  ))
}

// ---------- Sprechblase ----------

function Bubble({ at, out, tone, inline, children }) {
  return (
    <span
      className={clsx(
        'v10-bubble',
        tone && `v10-bubble--${tone}`,
        inline && 'is-inline'
      )}
      style={inline ? undefined : anim({ at, out, y: 14, oy: -10, s: 0.6 })}
    >
      {children}
    </span>
  )
}

Bubble.propTypes = {
  at: PropTypes.arrayOf(PropTypes.number),
  out: PropTypes.arrayOf(PropTypes.number),
  tone: PropTypes.string,
  inline: PropTypes.bool,
  children: PropTypes.node
}

// ---------- Zierformen am Rand ----------

const DECOR = [
  { kind: 'tri', color: 'yellow', size: 70, x: 120, y: 200, r: -14 },
  { kind: 'sq', color: 'green', size: 46, x: 1580, y: 180, r: 12 },
  { kind: 'dot', color: 'pink', size: 52, x: 2300, y: 820 },
  { kind: 'tri', color: 'blue', size: 56, x: 150, y: 1400, r: 18 },
  { kind: 'sq', color: 'pink', size: 38, x: 1680, y: 1420, r: -10 },
  { kind: 'dot', color: 'yellow', size: 34, x: 840, y: 820 }
]

function Decor() {
  return DECOR.map((d, i) => (
    <At
      key={i}
      x={d.x}
      y={d.y}
      className='v10-decor'
      style={{ '--d': `${-i * 1.3}s` }}
    >
      <Shape kind={d.kind} color={d.color} size={d.size} rotate={d.r} />
    </At>
  ))
}

// ---------- Ganze Welt ----------

export function World() {
  return (
    <>
      <Board />
      <Decor />
      <Labels />
      <Reach />
      <GameTiles />
      <Pool />
      <Podium />
      <TeamBack />
      <HrBack />
      <SalesBack />
      <TeamFront />
      <HrFront />
      <SalesFront />
      <AnalysisDots />
      <Flood />
      <Scanner />
      <Candidates />
      <Swimmers />
      <AylinWalk />
      <Newcomers />
      <Confetti x={SEAT.x} y={540} at={b('start', 0.44, 0.7)} />
      <Milestones />
      <At x={2050} y={1010}>
        <span
          className='v10-pill v10-pill--blue v10-pooltag'
          style={anim({ at: b('absagen', 0.6, 0.7), y: 10, s: 0.6 })}
        >
          4 Talente passen auf eure nächsten Stellen
        </span>
      </At>
    </>
  )
}
